import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  MessageSquarePlus,
  PanelRightClose,
  Sparkles,
  Trash2,
} from 'lucide-react';
import type { AssistantConversation } from '@xtreed/academy-shared';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useConfirm,
} from '@xtreed/academy-shared/ui';

interface AssistantHeaderProps {
  conversations: AssistantConversation[];
  activeConversationId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

export function AssistantHeader({
  conversations,
  activeConversationId,
  onSelect,
  onNew,
  onDelete,
  onClose,
}: AssistantHeaderProps) {
  const { t } = useTranslation('adminAssistant');
  const confirm = useConfirm();
  const active = conversations.find(
    (conversation) => conversation.id === activeConversationId,
  );

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border/50 px-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary-ink">
        <Sparkles className="h-4 w-4" />
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex min-w-0 flex-1 flex-col items-start rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-secondary/60"
          >
            <span className="text-sm leading-tight font-semibold text-foreground">
              {t('panel.title')}
            </span>
            <span className="flex w-full min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
              <span className="truncate">
                {active?.title || t('panel.subtitle')}
              </span>
              <ChevronDown className="h-3 w-3 shrink-0" />
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 glass">
          <DropdownMenuLabel className="text-xs">
            {t('panel.conversations')}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {conversations.length === 0 && (
            <p className="px-2 py-3 text-xs text-muted-foreground">
              {t('panel.noConversations')}
            </p>
          )}
          <div className="max-h-72 overflow-y-auto">
            {conversations.map((conversation) => (
              <DropdownMenuItem
                key={conversation.id}
                onSelect={() => onSelect(conversation.id)}
                className={cn(
                  'group flex cursor-pointer items-center gap-2',
                  conversation.id === activeConversationId && 'bg-primary/10',
                )}
              >
                <span className="min-w-0 flex-1 truncate text-sm">
                  {conversation.title || t('panel.untitled')}
                </span>
                <button
                  type="button"
                  aria-label={t('panel.deleteConversation')}
                  onClick={async (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const ok = await confirm({
                      description: t('panel.deleteConfirm'),
                      variant: 'destructive',
                    });
                    if (ok) onDelete(conversation.id);
                  }}
                  className="rounded p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuItem>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        type="button"
        onClick={onNew}
        title={t('panel.newConversation')}
        aria-label={t('panel.newConversation')}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
      >
        <MessageSquarePlus className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onClose}
        title={t('panel.close')}
        aria-label={t('panel.close')}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
      >
        <PanelRightClose className="h-4 w-4" />
      </button>
    </header>
  );
}
